import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { SupplierInstallationEmissionReportDto } from '@/features/suppliers/hooks/use-supplier-installation-emission-reports'
import {
  buildSupplierInstallationEmissionReportValidCnCodeSet,
  isSupplierInstallationEmissionReportCnCodeValid,
  normalizeSupplierInstallationEmissionReportCnCode,
} from '@/features/suppliers/lib/supplier-installation-emission-report-cn-code'
import { useCbamGoodsCodesQuery } from '@/features/installations/hooks/useInstallationConfiguration'
import {
  isSupplierInstallationEmissionReportProofFileSizeValid,
  isSupplierInstallationEmissionReportProofFileTypeValid,
  supplierInstallationEmissionReportProofFileAccept,
} from '@/features/suppliers/lib/supplier-installation-emission-report-proof-file'
import {
  formatSupplierInstallationEmissionReportProductionRouteLabel,
  supplierInstallationEmissionReportProductionRouteValues,
} from '@/features/suppliers/lib/supplier-installation-emission-report-production-route-label'
import {
  supplierInstallationEmissionReportYearValues,
  isSupplierInstallationEmissionReportSpecificEmbeddedEmissionsValid,
  supplierInstallationEmissionReportSpecificEmbeddedEmissionsMax,
  type SupplierInstallationEmissionReportProductionRoute,
  type SupplierInstallationEmissionReportYear,
} from '@artem-cbam-operator/common-server/supplier-installation-emission-report'
import { SupplierInstallationEmissionReportCnCodeSelect } from '@/features/suppliers/components/supplier-installation-emission-report-cn-code-select'
import { useOrganizationDocuments } from '@/shared/hooks/use-organization-documents'
import { useSession } from '@/shared/hooks/useSession'
import { preventDialogDismissForCombobox } from '@/shared/lib/dialog-combobox'
import { DialogPortalContainer } from '@/shared/lib/dialog-portal-container'

type SupplierInstallationEmissionReportFormDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  report?: SupplierInstallationEmissionReportDto | null
  isSubmitting?: boolean
  onCreate: (payload: {
    year: SupplierInstallationEmissionReportYear
    cnCode: string
    productionRoute: SupplierInstallationEmissionReportProductionRoute
    specificEmbeddedEmissions: number
    proofFilePath: string
  }) => Promise<void>
  onEdit: (payload: {
    cnCode: string
    productionRoute?: SupplierInstallationEmissionReportProductionRoute
    proofFilePath?: string | null
  }) => Promise<void>
}

const defaultReportYear = supplierInstallationEmissionReportYearValues[0]

export function SupplierInstallationEmissionReportFormDialog({
  open,
  onOpenChange,
  mode,
  report,
  isSubmitting = false,
  onCreate,
  onEdit,
}: SupplierInstallationEmissionReportFormDialogProps) {
  const { t } = useTranslation(['installations', 'common'])
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()
  const { uploadDocument, downloadDocument } = useOrganizationDocuments({
    organizationId,
  })

  const [year, setYear] = useState(String(defaultReportYear))
  const [cnCode, setCnCode] = useState('')
  const [productionRoute, setProductionRoute] = useState<
    SupplierInstallationEmissionReportProductionRoute | ''
  >('')
  const [specificEmbeddedEmissions, setSpecificEmbeddedEmissions] = useState('')
  const [proofFilePath, setProofFilePath] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const uploadAbortControllerRef = useRef<AbortController | null>(null)
  const uploadGenerationRef = useRef(0)

  const resetForm = useCallback(() => {
    uploadAbortControllerRef.current?.abort()
    uploadAbortControllerRef.current = null
    setIsUploading(false)

    if (mode === 'edit' && report) {
      setYear(String(report.year))
      setCnCode(report.cnCode)
      setProductionRoute(report.productionRoute)
      setProofFilePath(report.proofFilePath)
      return
    }
    setYear(String(defaultReportYear))
    setCnCode('')
    setProductionRoute('')
    setSpecificEmbeddedEmissions('')
    setProofFilePath(null)
  }, [mode, report])

  useEffect(() => {
    if (open) {
      resetForm()
    }
  }, [open, resetForm])

  const normalizedCnCode = normalizeSupplierInstallationEmissionReportCnCode(cnCode)
  const goodsQuery = useCbamGoodsCodesQuery({ enabled: open })
  const validCnCodes = useMemo(
    () => buildSupplierInstallationEmissionReportValidCnCodeSet(goodsQuery.data),
    [goodsQuery.data]
  )
  const isCnCodeValid = useMemo(() => {
    if (
      mode === 'edit' &&
      report &&
      normalizedCnCode === normalizeSupplierInstallationEmissionReportCnCode(report.cnCode)
    ) {
      return true
    }
    return isSupplierInstallationEmissionReportCnCodeValid(
      normalizedCnCode,
      validCnCodes
    )
  }, [mode, normalizedCnCode, report, validCnCodes])

  const parsedYear = Number.parseInt(year, 10)
  const isYearValid = (
    supplierInstallationEmissionReportYearValues as readonly number[]
  ).includes(parsedYear)
  const parsedSpecificEmbeddedEmissions = Number.parseFloat(specificEmbeddedEmissions)
  const isSpecificEmbeddedEmissionsValid =
    specificEmbeddedEmissions.trim() !== '' &&
    Number.isFinite(parsedSpecificEmbeddedEmissions) &&
    isSupplierInstallationEmissionReportSpecificEmbeddedEmissionsValid(
      parsedSpecificEmbeddedEmissions
    )
  const isProductionRouteValid = productionRoute !== ''
  const isCreateValid =
    isYearValid &&
    isCnCodeValid &&
    isProductionRouteValid &&
    isSpecificEmbeddedEmissionsValid &&
    Boolean(proofFilePath)
  const isEditValid = isCnCodeValid && isProductionRouteValid

  const isSubmitDisabled = useMemo(() => {
    if (isSubmitting || isUploading) {
      return true
    }
    return mode === 'create' ? !isCreateValid : !isEditValid
  }, [isSubmitting, isUploading, mode, isCreateValid, isEditValid])

  const hasExistingProofFile = Boolean(proofFilePath)
  const hasPendingProofFileReplacement = useMemo(() => {
    if (mode !== 'edit' || !report?.proofFilePath || !proofFilePath) {
      return false
    }
    return proofFilePath !== report.proofFilePath
  }, [mode, proofFilePath, report?.proofFilePath])

  const onFileChange = useCallback(
    async (evt: React.ChangeEvent<HTMLInputElement>) => {
      const file = evt.target.files?.[0]
      evt.target.value = ''
      if (!file) {
        return
      }
      if (!isSupplierInstallationEmissionReportProofFileTypeValid(file)) {
        toast.error(
          t(
            'installations:suppliers.edit.installations.edit.emissionReports.errors.invalidProofFileType'
          )
        )
        return
      }
      if (!isSupplierInstallationEmissionReportProofFileSizeValid(file)) {
        toast.error(t('common:errors.uploadFile.fileTooLarge'))
        return
      }

      uploadAbortControllerRef.current?.abort()
      const abortController = new AbortController()
      uploadAbortControllerRef.current = abortController
      const uploadGeneration = uploadGenerationRef.current + 1
      uploadGenerationRef.current = uploadGeneration

      setIsUploading(true)
      try {
        const result = await uploadDocument(file, { signal: abortController.signal })
        if (uploadGeneration !== uploadGenerationRef.current) {
          return
        }
        if (!result.success || !result.fileName) {
          if (result.errorMessage === 'UPLOAD_ABORTED') {
            return
          }
          if (result.errorMessage === 'FILE_TOO_LARGE') {
            toast.error(t('common:errors.uploadFile.fileTooLarge'))
            return
          }
          if (result.errorMessage === 'UPLOAD_TIMED_OUT') {
            toast.error(t('common:errors.uploadFile.uploadTimedOut'))
            return
          }
          toast.error(t('common:errors.uploadFile.generic'))
          return
        }
        setProofFilePath(result.fileName)
      } finally {
        if (uploadGeneration === uploadGenerationRef.current) {
          setIsUploading(false)
          uploadAbortControllerRef.current = null
        }
      }
    },
    [uploadDocument, t]
  )

  const onDownloadProof = useCallback(async () => {
    if (!proofFilePath) {
      return
    }
    const result = await downloadDocument(proofFilePath)
    if (!result.success) {
      toast.error(t('common:errors.downloadFile.generic'))
      return
    }
    toast.success(t('common:notifications.downloadSuccess'))
  }, [downloadDocument, proofFilePath, t])

  const handleSubmit = useCallback(async () => {
    if (mode === 'create') {
      if (!isCreateValid || !proofFilePath) {
        return
      }
      await onCreate({
        year: parsedYear as SupplierInstallationEmissionReportYear,
        cnCode: normalizedCnCode,
        productionRoute,
        specificEmbeddedEmissions: parsedSpecificEmbeddedEmissions,
        proofFilePath,
      })
      return
    }

    if (!isEditValid || !report) {
      return
    }

    const payload: {
      cnCode: string
      productionRoute?: SupplierInstallationEmissionReportProductionRoute
      proofFilePath?: string | null
    } = {
      cnCode: normalizedCnCode,
    }

    if (productionRoute !== report.productionRoute) {
      payload.productionRoute = productionRoute
    }

    if (proofFilePath !== report.proofFilePath) {
      payload.proofFilePath = proofFilePath
    }

    await onEdit(payload)
  }, [
    mode,
    isCreateValid,
    isEditValid,
    proofFilePath,
    onCreate,
    parsedYear,
    normalizedCnCode,
    productionRoute,
    parsedSpecificEmbeddedEmissions,
    onEdit,
    report,
  ])

  const title =
    mode === 'create'
      ? t('installations:suppliers.edit.installations.edit.emissionReports.dialog.createTitle')
      : t('installations:suppliers.edit.installations.edit.emissionReports.dialog.editTitle')

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen)
        if (!nextOpen) {
          resetForm()
        }
      }}
    >
      <DialogContent
        className="max-w-lg"
        showCloseButton
        onInteractOutside={preventDialogDismissForCombobox}
        onPointerDownOutside={preventDialogDismissForCombobox}
      >
        <DialogPortalContainer>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {mode === 'create' ? (
            <div className="grid gap-2">
              <Label htmlFor="supplier-emission-report-year">
                {t(
                  'installations:suppliers.edit.installations.edit.emissionReports.fields.year'
                )}
                <span className="text-destructive"> *</span>
              </Label>
              <Select
                value={year}
                onValueChange={setYear}
              >
                <SelectTrigger id="supplier-emission-report-year" className="w-full">
                  <SelectValue
                    placeholder={t(
                      'installations:suppliers.edit.installations.edit.emissionReports.fields.yearPlaceholder'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {supplierInstallationEmissionReportYearValues.map((reportYear) => (
                    <SelectItem key={reportYear} value={String(reportYear)}>
                      {reportYear}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : report ? (
            <div className="grid gap-2">
              <Label>
                {t(
                  'installations:suppliers.edit.installations.edit.emissionReports.fields.year'
                )}
              </Label>
              <p className="text-sm">{report.year}</p>
            </div>
          ) : null}

          <div className="grid gap-2">
            <Label htmlFor="supplier-emission-report-cn-code">
              {t('installations:configuration.table.cnCode')}
              <span className="text-destructive"> *</span>
            </Label>
            <SupplierInstallationEmissionReportCnCodeSelect
              id="supplier-emission-report-cn-code"
              value={cnCode}
              onValueChange={setCnCode}
              active={open}
              aria-invalid={cnCode.length > 0 && !isCnCodeValid}
              placeholder={t('installations:configuration.cnPlaceholder')}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="supplier-emission-report-production-route">
              {t(
                'installations:suppliers.edit.installations.edit.emissionReports.fields.productionRoute'
              )}
              <span className="text-destructive"> *</span>
            </Label>
            <Select
              value={productionRoute || undefined}
              onValueChange={(value) =>
                setProductionRoute(value as SupplierInstallationEmissionReportProductionRoute)
              }
            >
              <SelectTrigger id="supplier-emission-report-production-route" className="w-full">
                <SelectValue
                  placeholder={t(
                    'installations:suppliers.edit.installations.edit.emissionReports.fields.productionRoutePlaceholder'
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {supplierInstallationEmissionReportProductionRouteValues.map((route) => (
                  <SelectItem key={route} value={route}>
                    {formatSupplierInstallationEmissionReportProductionRouteLabel(route, t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {mode === 'create' ? (
            <div className="grid gap-2">
              <Label htmlFor="supplier-emission-report-specific-embedded-emissions">
                {t(
                  'installations:suppliers.edit.installations.edit.emissionReports.fields.specificEmbeddedEmissions'
                )}
                <span className="text-destructive"> *</span>
              </Label>
              <Input
                id="supplier-emission-report-specific-embedded-emissions"
                type="number"
                min={0}
                max={supplierInstallationEmissionReportSpecificEmbeddedEmissionsMax}
                step={0.00001}
                value={specificEmbeddedEmissions}
                onChange={(event) => setSpecificEmbeddedEmissions(event.target.value)}
                placeholder={t(
                  'installations:suppliers.edit.installations.edit.emissionReports.fields.specificEmbeddedEmissionsPlaceholder'
                )}
              />
              {specificEmbeddedEmissions.trim().length > 0 &&
              !isSpecificEmbeddedEmissionsValid ? (
                <p className="text-sm text-destructive">
                  {t(
                    'installations:suppliers.edit.installations.edit.emissionReports.errors.invalidSpecificEmbeddedEmissions'
                  )}
                </p>
              ) : null}
            </div>
          ) : report ? (
            <div className="grid gap-2">
              <Label>
                {t(
                  'installations:suppliers.edit.installations.edit.emissionReports.fields.specificEmbeddedEmissions'
                )}
              </Label>
              <p className="text-sm tabular-nums">
                {report.specificEmbeddedEmissions}{' '}
                {t('common:units.tCO2ePerT')}
              </p>
            </div>
          ) : null}

          <div className="grid gap-2">
            <Label>
              {t(
                'installations:suppliers.edit.installations.edit.emissionReports.fields.proofFile'
              )}
              {mode === 'create' ? <span className="text-destructive"> *</span> : null}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t(
                'installations:suppliers.edit.installations.edit.emissionReports.fields.proofFileSingleDocumentHint'
              )}
            </p>
            <p className="text-sm text-muted-foreground">
              {t(
                'installations:suppliers.edit.installations.edit.emissionReports.fields.proofFileAllowedTypes'
              )}{' '}
              {t(
                'installations:suppliers.edit.installations.edit.emissionReports.fields.proofFileMaxSize'
              )}
            </p>
            {mode === 'edit' && report?.proofFilePath ? (
              <p className="text-sm text-muted-foreground">
                {t(
                  'installations:suppliers.edit.installations.edit.emissionReports.fields.proofFileReplaceHint'
                )}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" disabled={isUploading} asChild>
                <label className="cursor-pointer">
                  {isUploading ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Upload className="size-4" />
                  )}
                  {hasExistingProofFile
                    ? t(
                        'installations:suppliers.edit.installations.edit.emissionReports.actions.replaceFile'
                      )
                    : t(
                        'installations:suppliers.edit.installations.edit.emissionReports.actions.uploadFile'
                      )}
                  <input
                    type="file"
                    className="sr-only"
                    accept={supplierInstallationEmissionReportProofFileAccept}
                    onChange={onFileChange}
                    disabled={isUploading}
                  />
                </label>
              </Button>
              {proofFilePath ? (
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-medium">
                    {t(
                      'installations:suppliers.edit.installations.edit.emissionReports.fields.currentProofFile'
                    )}
                  </p>
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto max-w-full px-0"
                    onClick={onDownloadProof}
                  >
                    <span className="truncate">{proofFilePath}</span>
                  </Button>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {t(
                    'installations:suppliers.edit.installations.edit.emissionReports.fields.noFile'
                  )}
                </span>
              )}
            </div>
            {hasPendingProofFileReplacement ? (
              <Alert>
                <AlertDescription>
                  {t(
                    'installations:suppliers.edit.installations.edit.emissionReports.fields.proofFilePendingReplacement',
                    { fileName: proofFilePath }
                  )}
                </AlertDescription>
              </Alert>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('installations:configuration.cancel')}
          </Button>
          <Button type="button" disabled={isSubmitDisabled} onClick={handleSubmit}>
            {mode === 'create' ? t('common:actions.create') : t('common:actions.save')}
          </Button>
        </DialogFooter>
        </DialogPortalContainer>
      </DialogContent>
    </Dialog>
  )
}
